import React, { useContext, useEffect } from "react";
import { useImmer } from "use-immer";
import { useDropdownApis } from "../../../shared";
import {
  errorMessage,
  formatAmount,
  getStoreId,
  successMessage,
} from "../../../utils";
import {
  downloadAgedDebtorsReport,
  getAgedDebtorsReportListApi,
  sendAgedDebtorsReport,
} from "../api/agedDebtors.api";
import qs from "qs";
import axios from "axios";
import fileDownload from "js-file-download";
import { TableStateContext } from "../../../store";
import { Link, useLocation } from "react-router-dom";
import moment from "moment";

export const useAgedDebtorsReport = () => {
  const PAGE_NAME = "agedDebtorsReport";
  const {
    filterState: {
      params: { agedDebtorsReport },
    },
    filterStateDispatch,
  } = useContext(TableStateContext);
  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("store-admin");
  const storeId = getStoreId();

  const [state, setState] = useImmer({
    isSendEmailDialogOpen: false,
    filter: {
      store: null,
      date: new Date(),
    },
    pagination: {
      pageIndex: 1,
      pageSize: 50,
      pageCount: 0,
      total: 0,
      hasMorePages: true,
      lastPage: 1,
    },
    isLoading: true,
    dataList: [],
    headers: [],
  });

  const { storeList } = useDropdownApis({ isStoreList: true });

  // Add useEffect to set initial store
  useEffect(() => {
    if (storeList?.length > 0 && !state.filter.store) {
      const { store, date } = agedDebtorsReport.filterApplied;
      const { pagination } = agedDebtorsReport;
      setState((draft) => {
        draft.filter.store = store || storeList[0];
        draft.filter.date = date ? new Date(date) : new Date();
        draft.pagination = pagination;
      });
    }
  }, [storeList, setState]);

  useEffect(() => {
    const store_id = isStoreAdmin ? storeId : state.filter.store?.id;
    if (store_id && state.isLoading) {
      const { pagination } = state;
      handleGetAgedDebtorsReport({ store_id: store_id, pagination });
    }
  }, [state.filter.store, storeId]);

  const breadcrumbs = [
    { name: "Dashboard", href: "/" },
    { name: "Aged Debtors Report", href: "" },
  ];

  const toggleSendEmailDialog = (open) => {
    setState((draft) => {
      draft.isSendEmailDialogOpen = open;
    });
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setState((draft) => {
      draft.filter[name] = value;
    });
    filterStateDispatch({
      type: "SET_DATA",
      page: PAGE_NAME,
      data: { filterApplied: { [name]: value } },
    });
  };

  const handleSendEmail = async (emails) => {
    try {
      // Get store_id based on user type
      const store_id = isStoreAdmin ? storeId : state.filter.store?.id;

      console.log(store_id, isStoreAdmin);

      if (!store_id) {
        errorMessage("Please select a store first");
        return;
      }
      const date = state.filter.date
        ? moment(state.filter.date).format("YYYY-MM-DD")
        : null;
      const emailData = {
        store_id,
        emails,
        date,
      };

      const res = await sendAgedDebtorsReport(emailData);

      if (res.success) {
        successMessage(res.message);
        toggleSendEmailDialog(false);
      } else {
        errorMessage(res.message);
      }
    } catch (err) {
      errorMessage(err.response.data.message);
      toggleSendEmailDialog(false);
    }
  };

  const handleDownloadEmail = async () => {
    try {
      let params = {};
      const store_id = isStoreAdmin ? storeId : state.filter.store?.id;
      const storeName = state.filter.store?.name;
      const date = state.filter.date
        ? moment(state.filter.date).format("YYYY-MM-DD")
        : null;

      if (!store_id) {
        errorMessage("Please select a store first");
        return;
      }

      params = { ...params, store_id };
      if (date) params.date = date;

      const query = {
        params,
        paramsSerializer: (params) => qs.stringify(params, { encode: true }),
      };

      const res = await downloadAgedDebtorsReport(query);

      // Check if response is empty or has no data
      if (!res.data || res.data.length === 0) {
        errorMessage("Data is not available");
        return;
      }

      const content = res.headers["content-type"];
      const currentDate = moment().format("YYYY-MM-DD"); // Format: YYYY-MM-DD
      const fileName = `aged-debtors-report-${storeName}-${currentDate}.pdf`;

      fileDownload(res.data, fileName, content);
    } catch (err) {
      errorMessage(err.response?.data?.message || "Download failed");
    }
  };

  const handleGetAgedDebtorsReport = async ({ pagination }) => {
    let params = {};
    const store_id = isStoreAdmin ? storeId : state.filter.store?.id;
    const date = state.filter.date
      ? moment(state.filter.date).format("YYYY-MM-DD")
      : null;

    params = { ...params, store_id: store_id };
    if (date) params.date = date;

    const query = {
      params,
      paramsSerializer: (params) => qs.stringify(params),
    };
    try {
      setState((draft) => {
        draft.isLoading = true;
      });
      const res = await getAgedDebtorsReportListApi(query, pagination);
      if (res.success) {
        const { current_page, last_page, per_page, total } =
          res.data.pagination;
        setState((draft) => {
          draft.isLoading = false;
          draft.dataList = res.data.result_data;
          // Capitalize headers
          draft.headers = handleCreateHeaders(res.data.headers);
          draft.pagination = {
            total: total,
            pageIndex: current_page,
            pageSize: per_page,
            pageCount: last_page,
            hasMorePages: current_page < last_page,
            lastPage: last_page,
          };
        });
      }
    } catch (err) {
      errorMessage(err.response.data.message);
      setState((draft) => {
        draft.isLoading = false;
      });
    }
  };

  const handleCreateHeaders = (headers) => {
    return headers.map((header) => {
      const headerText =
        header.percentage_total_name || header.total_name || header.header;
      const baseHeader = {
        ...header,
        Header: headerText,
        disableSortBy: true,
        ...(header.total_name && { is_total: true }),
        ...(header.percentage_total_name && { is_percentage_total: true }),
      };

      if (header.accessor === "client_account_name") {
        return {
          ...baseHeader,
          Cell: ({ row: { original } }) => {
            const displayText =
              original[header.accessor] ||
              original.total_name ||
              original.percentage_total_name;
            const isTotal =
              original.total_name || original.percentage_total_name;

            if (isTotal) {
              return <span>{displayText}</span>;
            }

            // Get client ID from the row data
            const clientId = original.client_account_id;
            const baseUrl = isStoreAdmin ? "/store-admin" : "";
            const clientUrl = `${baseUrl}/client-account-v2/details/${clientId}`;

            return (
              <Link
                to={clientUrl}
                style={{
                  textDecoration: "underline",
                  color: "#000",
                  cursor: "pointer",
                }}
              >
                {displayText}
              </Link>
            );
          },
        };
      }

      // Default cell renderer for numeric columns
      return {
        ...baseHeader,
        Cell: ({ row: { original } }) => {
          const value = original[header.accessor];
          if (value != null) {
            const numericValue =
              typeof value === "string" ? parseFloat(value) : value;
            if (isNaN(numericValue)) {
              return <span>--</span>;
            }
            // Add % for percentage total rows, otherwise show with $
            if (original.percentage_total_name) {
              return <span>{numericValue.toFixed(2)}%</span>;
            }
            return <span>{formatAmount(numericValue)}</span>;
          }
          return <span>--</span>;
        },
      };
    });
  };

  const gotoPage = (pageIndex) => {
    setState((draft) => {
      draft.pagination.pageIndex = pageIndex;
    });
    handleGetAgedDebtorsReport({
      store_id: isStoreAdmin ? storeId : state.filter.store?.id,
      pagination: { ...state.pagination, pageIndex },
    });

    filterStateDispatch({
      type: "UPDATE_PAGE_INDEX",
      page: PAGE_NAME,
      value: pageIndex,
    });
  };

  const setPageSize = (e) => {
    setState((draft) => {
      draft.pagination.pageSize = +e.target.value;
      draft.pagination.pageIndex = 1; // Reset to first page when changing page size
    });
    handleGetAgedDebtorsReport({
      store_id: isStoreAdmin ? storeId : state.filter.store?.id,
      pagination: { pageSize: +e.target.value, pageIndex: 1 },
    });
    filterStateDispatch({
      type: "UPDATE_PAGE_SIZE",
      page: PAGE_NAME,
      value: +e.target.value,
    });
  };

  const handleApplyFilter = () => {
    handleGetAgedDebtorsReport({
      pagination: { pageSize: state.pagination.pageSize, pageIndex: 1 },
    });
  };

  return {
    state,
    storeList,
    breadcrumbs,
    isStoreAdmin,
    handleDownloadEmail,
    toggleSendEmailDialog,
    handleSendEmail,
    handleFilterChange,
    gotoPage,
    setPageSize,
    handleApplyFilter,
  };
};
